import { GenericAbortSignal } from 'axios';

import type { Client as ClientType } from 'models/flexible/Client';
import { Client, ClientOptions } from 'models/flexible/Client';
import { Paginated } from 'models/flexible/Paginated';
import type {
  AdminClientCreateParams,
  AdminClientIndexFields,
  AdminClientIndexInclude,
  AdminClientShowFields,
  AdminClientShowInclude,
  AdminClientUpdateParams,
} from 'services/api/fields';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const adminClientsQueryKeys = {
  index: (params?: Record<string, any>) => ['adminClients', params] as const,
  show: (id: string) => ['adminClients', id] as const,
  // Clients eligible to start a denial list (the "Add New List" picker). Invalidated whenever a
  // list is created or deleted, since that changes which clients are eligible.
  denialListEligible: ['adminClients', 'denialListEligible'] as const,
  // Clients eligible for a high cost medication list upload. Reset whenever a list is created or removed,
  // since that changes which clients are eligible.
  highCostMedListEligible: ['adminClients', 'highCostMedListEligible'] as const,
};

const URL = 'admin/clients';

export type AdminClientIndexParams = BaseIndexQueryParams & {
  'clientType.not'?: string;
  leaf?: boolean;
  'id.not'?: string;
  aiPrompt?: string;
  'aiPrompt.not'?: string;
  clientType?: string;
  productAccess?: string;
  hasImportConfiguration?: boolean;
  denialListEligible?: boolean;
  highCostMedListEligible?: boolean;
  isPayingCustomer?: boolean;
  isContracted?: boolean;
};

export async function adminIndexClients<I extends AdminClientIndexInclude = never>(
  params: AdminClientIndexParams,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Paginated<ClientType<AdminClientIndexFields<I>>>> {
  return http
    .get<
      Paginated<ClientOptions>
    >(URL, { params: { ...params, ...(includes?.length ? { include: includes.join(',') } : {}) }, signal })
    .then((res) => ({
      ...res.data,
      data: res.data.data.map((x) => new Client(x)),
    })) as unknown as Promise<Paginated<ClientType<AdminClientIndexFields<I>>>>;
}

export function adminShowClient<I extends AdminClientShowInclude = never>(
  id: string,
  signal?: GenericAbortSignal,
  includes?: readonly I[]
): Promise<ClientType<AdminClientShowFields<I>>> {
  return http
    .get(`${URL}/${id}`, {
      params: { include: includes?.join(',') },
      signal,
    })
    .then((res) => new Client(res.data)) as unknown as Promise<ClientType<AdminClientShowFields<I>>>;
}

export async function adminCreateClient(params: AdminClientCreateParams) {
  return http.post<ClientOptions>(URL, params).then((res) => new Client(res.data));
}

export async function adminUpdateClient(id: string, params: AdminClientUpdateParams) {
  return http.patch<ClientOptions>(`${URL}/${id}`, params).then((res) => new Client(res.data));
}
